import type { SPAPillTone } from '../shared/SPAPill';

// The list and the detail view answer the same question at different resolutions. The list starts
// from scores that exist, so it can't tell "withheld on purpose" from "retrying" without scoring
// every student — it reports the superset `not_delivered`, which the detail view then refines.
// They are never allowed to contradict each other, only to differ in precision.
export type ListState = 'in_sync' | 'out_of_sync' | 'not_delivered';

export type DetailState =
  | 'in_sync' | 'out_of_sync' | 'never_synced' | 'retrying' | 'not_due' | 'no_score';

export const STATE_LABELS: Record<ListState | DetailState, string> = {
  in_sync: 'In sync',
  never_synced: 'Never synced',
  no_score: 'No score yet',
  not_delivered: 'Not delivered',
  not_due: 'Not due yet',
  out_of_sync: 'Out of sync',
  retrying: 'Retrying',
};

export const STATE_TONES: Record<ListState | DetailState, SPAPillTone> = {
  in_sync: 'success',
  never_synced: 'error',
  no_score: 'neutral',
  not_delivered: 'error',
  not_due: 'neutral',
  out_of_sync: 'error',
  retrying: 'warning',
};

export const REASON_LABELS: Record<string, string> = {
  attempts_exhausted: 'LMS assignment has used up its allowed attempts',
  line_item_missing: 'LMS assignment no longer exists',
  lms_auth_failed: 'The LMS rejected our credentials',
  lms_disconnected: 'The district disconnected its LMS integration',
  lms_unavailable: 'The LMS errored or timed out',
  never_attempted: 'Never attempted',
  not_launched: 'Never launched from the LMS',
  student_not_in_lms: 'Student is not in this LMS course',
  student_not_linked: 'Student has never launched from the LMS',
  superseded: 'A newer score had already been posted',
  throttled_by_lms: 'Rate limited by the LMS',
  throttled_by_tci: 'Held back by our rate limiter',
  unknown: 'Unrecognised failure',
  unlinked: 'Not linked to an LMS assignment',
  unpublished: 'LMS assignment is unpublished',
  zero_score_maximum: 'Worth zero points; the LMS rejects the score',
};
